import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/PackageProposal.graphql';
import { Query, QuerypackageProposalArgs } from '../../__generated__/globalTypes';

export const usePackageProposalQuery = (options?: QueryHookOptions<Query, QuerypackageProposalArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerypackageProposalArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const usePackageProposalLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerypackageProposalArgs>
): QueryResult<Query, QuerypackageProposalArgs> & {
  getPackageProposal: LazyQueryExecFunction<Query, QuerypackageProposalArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getPackageProposal, result] = useLazyQuery<Query, QuerypackageProposalArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getPackageProposal, ...result };
};

export const usePackageProposalPromiseQuery = () => {
  const client = useApolloClient();

  const getPackageProposal = useCallback(
    (options?: Omit<QueryOptions<QuerypackageProposalArgs, Query>, 'query'>) =>
      client.query<Query, QuerypackageProposalArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getPackageProposal };
};
// Suspense Query Hooks
export const usePackageProposalSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerypackageProposalArgs>
) => {
  const result = useSuspenseQuery<Query, QuerypackageProposalArgs>(QUERY, {
    ...options
  });

  return result;
};
